import React, { useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Leaf, Coins, ShieldCheck, Siren, Video, Trophy, Check, ArrowRight, Menu, X, LogIn, UserPlus, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { LanguageSelector } from '@/components/LanguageSelector';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/* ── helpers ── */
const PROD = 'https://ucleanapp.com.br';

function Section({ children, className = '', id }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.section
      ref={ref}
      id={id}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: 'easeOut' }}
      className={`relative ${className}`}
    >
      {children}
    </motion.section>
  );
}

function SectionLabel({ children }) {
  return <span className="text-xs uppercase tracking-[0.2em] font-bold text-green-400 mb-4 block">{children}</span>;
}

/* ── CTA Buttons (Login-focused) ── */
function CTAGroup({ size = 'default', onLogin, onRegister, onGoogle }) {
  const base = size === 'lg'
    ? 'px-7 py-4 min-h-[52px] text-base rounded-xl'
    : 'px-5 py-3 min-h-[48px] text-sm rounded-lg';
  return (
    <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
      <button type="button" onClick={onLogin} aria-haspopup="dialog" data-testid="cta-login"
        className={`${base} font-semibold text-white bg-purple-700 hover:bg-purple-600 hover:shadow-[0_0_24px_rgba(107,33,168,0.5)] transition-all text-center flex items-center justify-center gap-2`}>
        <LogIn className="h-4 w-4" /> Já é membro
      </button>
      <button type="button" onClick={onRegister} aria-haspopup="dialog" data-testid="cta-register"
        className={`${base} font-semibold text-white bg-green-600 hover:bg-green-500 hover:shadow-[0_0_24px_rgba(34,197,94,0.5)] transition-all text-center flex items-center justify-center gap-2`}>
        <UserPlus className="h-4 w-4" /> Criar conta
      </button>
      <button type="button" onClick={onGoogle} data-testid="cta-google"
        className={`${base} font-semibold text-white border border-white/20 bg-white/5 hover:bg-white/10 transition-all text-center flex items-center justify-center gap-2`}>
        <svg className="h-4 w-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
        Entrar com Google
      </button>
    </div>
  );
}

/* ── Login Modal ── */
function LoginModal({ open, onClose }) {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password, true);
      toast.success('Login realizado!');
      onClose();
      navigate('/feed', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'E-mail ou senha incorretos');
    } finally { setLoading(false); }
  };

  const handleGoogle = () => {
    const pendingRef = new URLSearchParams(window.location.search).get('ref') || document.getElementById('modal-register-invite')?.value;
    if (pendingRef) localStorage.setItem('uclean_pending_ref', pendingRef.trim());
    const redirectUrl = window.location.origin + '/auth/google/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto bg-[#0F0F1A] border-white/10" data-testid="login-modal">
        <DialogHeader>
          <DialogTitle className="text-white text-center text-xl">Bem-vindo de volta</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <div>
            <Label htmlFor="modal-login-email" className="text-zinc-300">E-mail</Label>
            <Input type="email" autoComplete="email" placeholder="seu@email.com" value={email} onChange={(e) => setEmail(e.target.value)} required className="bg-white/5 border-white/10 text-white" id="modal-login-email" data-testid="modal-login-email" />
          </div>
          <div>
            <Label htmlFor="modal-login-password" className="text-zinc-300">Senha</Label>
            <Input type="password" placeholder="********" value={password} onChange={(e) => setPassword(e.target.value)} required className="bg-white/5 border-white/10 text-white" id="modal-login-password" data-testid="modal-login-password" />
          </div>
          <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700 text-white" disabled={loading} data-testid="modal-login-submit">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <LogIn className="h-4 w-4 mr-2" />}
            Entrar
          </Button>
          <div className="relative my-1">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10"></div></div>
            <div className="relative flex justify-center text-xs"><span className="bg-[#0F0F1A] px-2 text-zinc-500">ou</span></div>
          </div>
          <Button type="button" variant="outline" className="w-full border-white/10 text-zinc-300 hover:bg-white/5 gap-2" onClick={handleGoogle} data-testid="modal-login-google">
            <svg className="h-4 w-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            Entrar com Google
          </Button>
          <div className="flex items-center justify-between text-xs">
            <Link to="/esqueci-senha" onClick={onClose} className="text-purple-400 hover:underline">Esqueceu a senha?</Link>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ── Register Modal ── */
function RegisterModal({ open, onClose }) {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [searchParams] = useSearchParams();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [inviteCode, setInviteCode] = useState(searchParams.get('ref') || '');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('Senha deve ter no mínimo 6 caracteres'); return; }
    setLoading(true);
    try {
      await register(email, password, name, 'user', inviteCode.trim());
      toast.success('Conta criada com sucesso!');
      onClose();
      navigate('/feed', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao criar conta');
    } finally { setLoading(false); }
  };

  const handleGoogle = () => {
    const pendingRef = new URLSearchParams(window.location.search).get('ref') || document.getElementById('modal-register-invite')?.value;
    if (pendingRef) localStorage.setItem('uclean_pending_ref', pendingRef.trim());
    const redirectUrl = window.location.origin + '/auth/google/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto bg-[#0F0F1A] border-white/10" data-testid="register-modal">
        <DialogHeader>
          <DialogTitle className="text-white text-center text-xl">Criar sua conta</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 py-2">
          <div>
            <Label htmlFor="modal-register-name" className="text-zinc-300">Nome</Label>
            <Input type="text" placeholder="Seu nome" value={name} onChange={(e) => setName(e.target.value)} required className="bg-white/5 border-white/10 text-white" id="modal-register-name" data-testid="modal-register-name" />
          </div>
          <div>
            <Label htmlFor="modal-register-email" className="text-zinc-300">E-mail</Label>
            <Input type="email" autoComplete="email" placeholder="seu@email.com" value={email} onChange={(e) => setEmail(e.target.value)} required className="bg-white/5 border-white/10 text-white" id="modal-register-email" data-testid="modal-register-email" />
          </div>
          <div>
            <Label htmlFor="modal-register-password" className="text-zinc-300">Senha</Label>
            <Input type="password" placeholder="Mínimo 6 caracteres" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="bg-white/5 border-white/10 text-white" id="modal-register-password" data-testid="modal-register-password" />
          </div>
          <div>
            <Label htmlFor="modal-register-invite" className="text-zinc-300 text-xs">Código de indicação <span className="text-zinc-500">(opcional)</span></Label>
            <Input type="text" placeholder="Ex: ABC123" value={inviteCode} onChange={(e) => setInviteCode(e.target.value)} className="bg-white/5 border-white/10 text-white" id="modal-register-invite" data-testid="modal-register-invite" />
          </div>
          <Button type="submit" className="w-full bg-green-600 hover:bg-green-700 text-white" disabled={loading} data-testid="modal-register-submit">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserPlus className="h-4 w-4 mr-2" />}
            Criar conta
          </Button>
          <div className="relative my-1">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10"></div></div>
            <div className="relative flex justify-center text-xs"><span className="bg-[#0F0F1A] px-2 text-zinc-500">ou</span></div>
          </div>
          <Button type="button" variant="outline" className="w-full border-white/10 text-zinc-300 hover:bg-white/5 gap-2" onClick={handleGoogle} data-testid="modal-register-google">
            <svg className="h-4 w-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            Entrar com Google
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ══════════════════════════════════════════════
   HEADER (sticky, glassmorphism)
   ══════════════════════════════════════════════ */
function Header({ onLogin, onRegister, onGoogle }) {
  const [open, setOpen] = React.useState(false);
  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-[#0F0F1A]/70 backdrop-blur-2xl border-b border-white/10" data-testid="landing-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2">
          <img
            src="https://customer-assets.emergentagent.com/job_d4175ee5-624c-4b49-aa70-897a010e6103/artifacts/uccjdqwq_favicon512x512.png"
            alt="U-Clean"
            className="h-10 w-10 object-contain"
            data-testid="header-logo"
          />
          <span className="text-xl font-black tracking-tighter text-white hidden sm:inline" style={{ fontFamily: 'Outfit, sans-serif' }}>U-Clean</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-6 text-sm text-zinc-400">
          <a href="#manifesto" className="hover:text-white transition-colors">Manifesto</a>
          <a href="#como-funciona" className="hover:text-white transition-colors">Como Funciona</a>
          <a href="#planos" className="hover:text-white transition-colors">Planos</a>
        </nav>

        {/* Desktop CTAs */}
        <div className="hidden md:flex items-center gap-2">
          <LanguageSelector />
          <button onClick={onLogin} className="px-4 py-2 text-sm text-white hover:text-purple-300 font-medium transition-colors" data-testid="header-login-btn">
            Entrar
          </button>
          <button onClick={onRegister} className="px-4 py-2 text-sm font-semibold text-white bg-green-600 hover:bg-green-500 rounded-lg transition-all" data-testid="header-register-btn">
            Criar conta
          </button>
        </div>

        {/* Mobile: login + burger */}
        <div className="flex md:hidden items-center gap-2">
          <LanguageSelector />
          <button onClick={onLogin} className="px-4 min-h-[44px] text-sm text-white bg-purple-700 rounded-lg font-semibold" aria-label="Já é membro, entrar" data-testid="mobile-login-btn">
            Entrar
          </button>
          <button onClick={() => setOpen(!open)} className="text-white p-2 min-h-[44px] min-w-[44px] flex items-center justify-center" aria-label="Abrir menu" aria-expanded={open} data-testid="mobile-menu-btn">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-[#0F0F1A]/95 backdrop-blur-2xl border-t border-white/10 overflow-hidden"
          >
            <div className="p-5 space-y-3">
              <a href="#manifesto" onClick={() => setOpen(false)} className="block text-zinc-400 hover:text-white py-1">Manifesto</a>
              <a href="#como-funciona" onClick={() => setOpen(false)} className="block text-zinc-400 hover:text-white py-1">Como Funciona</a>
              <a href="#planos" onClick={() => setOpen(false)} className="block text-zinc-400 hover:text-white py-1">Planos</a>
              <div className="pt-3 space-y-2">
                <button onClick={() => { setOpen(false); onLogin(); }} className="w-full py-3 text-sm font-semibold text-white bg-purple-700 rounded-lg flex items-center justify-center gap-2">
                  <LogIn className="h-4 w-4" /> Já é membro
                </button>
                <button onClick={() => { setOpen(false); onRegister(); }} className="w-full py-3 text-sm font-semibold text-white bg-green-600 rounded-lg flex items-center justify-center gap-2">
                  <UserPlus className="h-4 w-4" /> Criar conta
                </button>
                <button onClick={() => { setOpen(false); onGoogle(); }} className="w-full py-3 text-sm font-semibold text-white border border-white/20 rounded-lg flex items-center justify-center gap-2">
                  <svg className="h-4 w-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                  Entrar com Google
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

/* ══════════════════════════════════════════════
   SECTION 1 — HERO
   ══════════════════════════════════════════════ */
function Hero({ onLogin, onRegister, onGoogle }) {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16" data-testid="hero-section">
      {/* BG image */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1770486036751-e55247238964?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjBwdXJwbGUlMjBuZXVyYWwlMjBuZXR3b3JrJTIwcGFydGljbGVzfGVufDB8fHx8MTc3NTY1MzAyNHww&ixlib=rb-4.1.0&q=85&w=1920"
          alt="" className="w-full h-full object-cover" loading="eager"
        />
        <div className="absolute inset-0 bg-[#0F0F1A]/60" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 text-center py-24 md:py-32">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <img
            src="https://customer-assets.emergentagent.com/job_d4175ee5-624c-4b49-aa70-897a010e6103/artifacts/uccjdqwq_favicon512x512.png"
            alt="U-Clean"
            className="w-24 h-24 sm:w-28 sm:h-28 mx-auto mb-8 object-contain"
            data-testid="hero-logo"
          />
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black tracking-tighter leading-none text-white mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}>
            Você não é o vício.<br />
            <span className="bg-gradient-to-r from-purple-400 to-green-400 bg-clip-text text-transparent">Você é o retorno.</span>
          </h1>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.7 }}
          className="text-base md:text-lg text-zinc-300 max-w-2xl mx-auto mb-10 leading-relaxed"
          style={{ fontFamily: 'Manrope, sans-serif' }}
        >
          A primeira rede social criada para quem decidiu retomar o controle da própria vida.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.4 }}
          className="flex justify-center"
        >
          <CTAGroup size="lg" onLogin={onLogin} onRegister={onRegister} onGoogle={onGoogle} />
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.6 }}
          className="text-xs text-zinc-500 mt-8 max-w-md mx-auto"
          style={{ fontFamily: 'Manrope, sans-serif' }}
        >
          Gratuito para começar. Sem compromisso. Sua privacidade é sagrada.
        </motion.p>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════
   SECTION 2 — MANIFESTO
   ══════════════════════════════════════════════ */
function Manifesto() {
  return (
    <Section id="manifesto" className="py-24 md:py-32 bg-[#0F0F1A]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-5 gap-12 items-center">
        {/* Sphere visual */}
        <div className="hidden lg:block lg:col-span-2 relative">
          <img
            src="https://images.unsplash.com/photo-1656231963843-3551d4c79e25?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwyfHxjb3NtaWMlMjBlbmVyZ3klMjBzcGhlcmUlMjBsaWdodCUyMGFic3RyYWN0JTIwZGFya3xlbnwwfHx8fDE3NzU2NTMwMjl8MA&ixlib=rb-4.1.0&q=85&w=600"
            alt="" className="w-full max-w-sm mx-auto opacity-80 rounded-2xl"
          />
        </div>

        {/* Text */}
        <div className="lg:col-span-3" data-testid="manifesto-section">
          <SectionLabel>Manifesto</SectionLabel>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight text-white mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Por que a U-Clean existe?
          </h2>
          <div className="space-y-5 text-zinc-400 leading-relaxed text-sm md:text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <p>Existe um momento na vida de um adicto em que ele olha no espelho e não se reconhece mais. Existe um momento na vida de um familiar em que ele percebe que também adoeceu junto. Existe um momento na vida de um profissional em que ele sente que as ferramentas que tem não são suficientes para acompanhar seus pacientes 24 horas por dia.</p>
            <p className="text-white font-medium text-base md:text-lg">A U-Clean nasceu nesses momentos.</p>
            <p>Não somos um aplicativo de meditação. Não somos uma clínica online. Não somos um grupo de WhatsApp.</p>
            <p>Somos uma rede. Uma comunidade real, construída por quem passou pelo fundo do poço e decidiu subir. Um lugar onde a sobriedade tem valor — literal e financeiro. Onde o familiar encontra paz sem precisar esperar o outro se recuperar primeiro. Onde o profissional tem uma vitrine qualificada e ferramentas reais de acompanhamento.</p>
            <p>Aqui, cada dia limpo floresce. Cada check-in vale. Cada história importa.</p>
            <p className="text-white font-medium">Você não precisa chegar ao fundo para pedir ajuda. Você só precisa decidir que hoje é o Dia 1.</p>
            <p className="text-purple-400 italic font-medium text-base md:text-lg mt-6">U-Clean. Social Network for Addicts.</p>
          </div>
        </div>
      </div>
    </Section>
  );
}

/* ══════════════════════════════════════════════
   SECTION 3 — TRÊS PILARES
   ══════════════════════════════════════════════ */
const PILLARS = [
  {
    icon: '🌱', color: 'purple', title: 'Sua sobriedade tem valor',
    text: 'Ganhe Tokens AWA por cada dia limpo, check-in diário e meta batida. Cultive seu Jardim da Alma e transforme sua recuperação em conquistas reais e financeiras.',
    cta: 'Fazer o Quiz de Recuperação', link: '/quiz/recuperacao', testid: 'pillar-adicto',
    label: 'O Adicto — A Retomada',
  },
  {
    icon: '🕊️', color: 'green', title: 'Sua recuperação não depende da dele',
    text: 'A co-dependência é silenciosa e devastadora. Na U-Clean, você encontra uma comunidade que entende sua dor, ferramentas para estabelecer limites e um espaço seguro para retomar a sua vida.',
    cta: 'Fazer o Quiz de Familiares', link: '/quiz/familiares', testid: 'pillar-familiar',
    label: 'O Familiar — A Paz',
  },
  {
    icon: '🏅', color: 'cyan', title: 'Seu consultório estendido',
    text: 'Acompanhe seus pacientes 24h, publique conteúdo para um público 100% segmentado e construa autoridade em uma rede criada para quem realmente precisa de você.',
    cta: 'Fazer o Quiz Profissional', link: '/quiz/profissionais', testid: 'pillar-profissional',
    label: 'O Profissional — A Vitrine',
  },
];

const PILLAR_STYLES = {
  purple: { border: 'hover:border-purple-500/50', glow: 'hover:shadow-[0_0_30px_rgba(107,33,168,0.2)]', btn: 'bg-purple-700 hover:bg-purple-600' },
  green: { border: 'hover:border-green-500/50', glow: 'hover:shadow-[0_0_30px_rgba(34,197,94,0.2)]', btn: 'bg-green-600 hover:bg-green-500' },
  cyan: { border: 'hover:border-cyan-500/50', glow: 'hover:shadow-[0_0_30px_rgba(6,182,212,0.2)]', btn: 'bg-cyan-600 hover:bg-cyan-500' },
};

function Pillars() {
  return (
    <Section id="como-funciona" className="py-24 md:py-32 bg-[#0F0F1A]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-16">
          <SectionLabel>Como Funciona</SectionLabel>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Uma plataforma. Três jornadas. Um objetivo: <span className="font-black">liberdade.</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {PILLARS.map((p, i) => {
            const s = PILLAR_STYLES[p.color];
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15, duration: 0.6 }}
                className={`bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 transition-all duration-300 ${s.border} ${s.glow} hover:-translate-y-1`}
                data-testid={p.testid}
              >
                <span className="text-4xl block mb-4">{p.icon}</span>
                <span className="text-xs uppercase tracking-[0.15em] text-zinc-500 font-bold">{p.label}</span>
                <h3 className="text-xl font-bold text-white mt-2 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>{p.title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>{p.text}</p>
                <Link to={p.link} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-all ${s.btn}`}>
                  {p.cta} <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </Section>
  );
}

/* ══════════════════════════════════════════════
   SECTION 4 — ECOSSISTEMA
   ══════════════════════════════════════════════ */
const FEATURES = [
  { Icon: Leaf, title: 'Jardim da Alma', text: 'Sua sobriedade visualizada. Cada dia limpo faz sua planta crescer.', span: 'md:col-span-4 lg:col-span-4' },
  { Icon: Coins, title: 'Tokens AWA', text: 'Ganhe recompensas reais por cuidar de você. 1000 AWA = R$ 1,00.', span: 'md:col-span-4 lg:col-span-4' },
  { Icon: ShieldCheck, title: 'Anonimato Total', text: 'Você decide o que compartilhar e com quem. Sua identidade é sagrada.', span: 'md:col-span-4 lg:col-span-4' },
  { Icon: Siren, title: 'Botão SOS', text: 'Está na fissura? Um clique conecta você à comunidade e aos mentores em tempo real.', span: 'md:col-span-6 lg:col-span-6' },
  { Icon: Video, title: 'Reuniões Online', text: 'Grupos de apoio ao vivo, todos os dias, para adictos e familiares.', span: 'md:col-span-6 lg:col-span-3' },
  { Icon: Trophy, title: 'NFT Árvore Viva', text: 'Conquiste NFTs únicos conforme sua sobriedade evolui. Sua história na blockchain.', span: 'md:col-span-6 lg:col-span-3' },
];

function Ecosystem() {
  return (
    <Section className="py-24 md:py-32 relative">
      <div className="absolute inset-0 opacity-20">
        <img
          src="https://images.unsplash.com/photo-1770968476272-69e3e4af5df7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRhcmslMjBwdXJwbGUlMjBuZXVyYWwlMjBuZXR3b3JrJTIwcGFydGljbGVzfGVufDB8fHx8MTc3NTY1MzAyNHww&ixlib=rb-4.1.0&q=85&w=1920"
          alt="" className="w-full h-full object-cover"
        />
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-16">
          <SectionLabel>Ecossistema</SectionLabel>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Tudo que você precisa para <span className="font-black">não estar sozinho nessa.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
          {FEATURES.map((f, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.5 }}
              className={`${f.span} col-span-1 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 lg:p-8 transition-all duration-300 hover:border-purple-500/40 hover:shadow-[0_0_20px_rgba(107,33,168,0.15)] group`}
              data-testid={`feature-${f.title.replace(/\s/g, '-').toLowerCase()}`}
            >
              <f.Icon className="w-8 h-8 text-green-400 mb-4 group-hover:text-purple-400 transition-colors" />
              <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{f.title}</h3>
              <p className="text-zinc-400 text-sm leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>{f.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
}

/* ══════════════════════════════════════════════
   SECTION 5 — PROVA SOCIAL
   ══════════════════════════════════════════════ */
const TESTIMONIALS = [
  {
    name: 'Fênix_RJ', badge: '90 dias limpo',
    quote: 'Nunca pensei que um app pudesse me fazer sentir que não estou sozinho às 3 da manhã quando a fissura bate.',
    avatar: 'https://images.unsplash.com/photo-1521817760127-e15c26f67fd2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxicmF6aWxpYW4lMjBwZXJzb24lMjBwb3J0cmFpdCUyMGhlYWRzaG90JTIwd2FybXxlbnwwfHx8fDE3NzU2NTMwNjB8MA&ixlib=rb-4.1.0&q=85&w=150&h=150',
  },
  {
    name: 'MãeForte_SP', badge: '2 anos acompanhando o filho',
    quote: 'Aprendi aqui que eu também precisava de tratamento. Minha paz não podia esperar ele decidir parar.',
    avatar: 'https://images.unsplash.com/photo-1648478445898-ae81dfa8701b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwyfHxicmF6aWxpYW4lMjBwZXJzb24lMjBwb3J0cmFpdCUyMGhlYWRzaG90JTIwd2FybXxlbnwwfHx8fDE3NzU2NTMwNjB8MA&ixlib=rb-4.1.0&q=85&w=150&h=150',
  },
  {
    name: 'Dr.Marcos_Terapeuta', badge: 'Membro verificado',
    quote: 'Finalmente uma plataforma onde meu trabalho chega para quem realmente precisa, no momento em que precisa.',
    avatar: 'https://images.unsplash.com/photo-1729795795344-3f102e3f9d37?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwzfHxicmF6aWxpYW4lMjBwZXJzb24lMjBwb3J0cmFpdCUyMGhlYWRzaG90JTIwd2FybXxlbnwwfHx8fDE3NzU2NTMwNjB8MA&ixlib=rb-4.1.0&q=85&w=150&h=150',
  },
];

function SocialProof() {
  return (
    <Section className="py-24 md:py-32 bg-[#0F0F1A]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-16">
          <SectionLabel>Depoimentos</SectionLabel>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Quem já decidiu <span className="font-black">falar:</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15, duration: 0.6 }}
              className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8"
              data-testid={`testimonial-${i}`}
            >
              <p className="text-zinc-300 text-sm md:text-base leading-relaxed mb-6 italic" style={{ fontFamily: 'Manrope, sans-serif' }}>
                "{t.quote}"
              </p>
              <div className="flex items-center gap-3">
                <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full object-cover" loading="lazy" />
                <div>
                  <p className="text-white text-sm font-bold">{t.name}</p>
                  <p className="text-zinc-500 text-xs">{t.badge}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
}

/* ══════════════════════════════════════════════
   SECTION 6 — PLANOS
   ══════════════════════════════════════════════ */
const PLANS = [
  {
    name: 'Gratuito', price: 'R$ 0', period: '/sempre', highlight: false, testid: 'pricing-free',
    features: ['Feed social completo', 'Check-in diário', '1 planta no Jardim', 'Tokens AWA básicos', 'Grupos de apoio público', 'Botão SOS'],
  },
  {
    name: 'Premium', price: 'R$ 29,90', period: '/mês', highlight: true, testid: 'pricing-premium-btn',
    features: ['Tudo do Gratuito', 'Até 3 plantas no Jardim', 'Badge Premium', 'Acesso prioritário a reuniões', 'Sem anúncios', 'Suporte dedicado', 'Programa PAREI-AD'],
    note: '11% off no trimestral | 30% off no anual',
  },
  {
    name: 'Profissional', price: 'Grátis', period: 'para começar', highlight: false, testid: 'pricing-professional',
    features: ['Perfil profissional público', 'Agendamento de consultas', 'Acompanhar pacientes', 'Selo de Especialista', 'Salas de supervisão', 'Certificação PAREI-AD'],
    note: 'Revenue Share 80/20 — Você recebe 80% do valor da consulta',
  },
];

function Pricing() {
  return (
    <Section id="planos" className="py-24 md:py-32 bg-[#0F0F1A]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-16">
          <SectionLabel>Planos</SectionLabel>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Comece de graça. <span className="font-black">Evolua no seu tempo.</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 max-w-5xl mx-auto">
          {PLANS.map((plan, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.12, duration: 0.6 }}
              data-testid={plan.testid}
              className={`relative rounded-2xl p-8 transition-all duration-300 ${
                plan.highlight
                  ? 'bg-purple-900/30 border-2 border-purple-500/60 shadow-[0_0_40px_rgba(107,33,168,0.2)]'
                  : 'bg-white/5 border border-white/10 hover:border-white/20'
              }`}
            >
              {plan.highlight && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-purple-600 text-white text-xs font-bold px-4 py-1 rounded-full uppercase tracking-wider">
                  Mais Popular
                </span>
              )}
              <h3 className="text-lg font-bold text-white mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{plan.name}</h3>
              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-3xl font-black text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{plan.price}</span>
                <span className="text-zinc-500 text-sm">{plan.period}</span>
              </div>
              <ul className="space-y-3 mb-8">
                {plan.features.map((f, j) => (
                  <li key={j} className="flex items-start gap-2 text-sm text-zinc-300" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              {plan.note && <p className="text-xs text-purple-300 mb-4">{plan.note}</p>}
              <Link
                to="/login?tab=register"
                className={`block text-center py-3 rounded-xl font-semibold text-sm transition-all ${
                  plan.highlight
                    ? 'bg-purple-600 text-white hover:bg-purple-500 hover:shadow-[0_0_20px_rgba(107,33,168,0.5)]'
                    : 'bg-white/10 text-white hover:bg-white/15'
                }`}
              >
                Começar Agora
              </Link>
            </motion.div>
          ))}
        </div>

        <p className="text-center text-zinc-600 text-xs mt-8 max-w-md mx-auto" style={{ fontFamily: 'Manrope, sans-serif' }}>
          O valor da assinatura é menor do que você gastava em um dia de vício. Invista na sua liberdade.
        </p>
      </div>
    </Section>
  );
}

/* ══════════════════════════════════════════════
   SECTION 7 — CTA FINAL
   ══════════════════════════════════════════════ */
function FinalCTA() {
  return (
    <Section className="py-24 md:py-32 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-purple-900/40 via-[#0F0F1A] to-[#0F0F1A]" />
      <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 text-center" data-testid="final-cta-section">
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Hoje pode ser o seu <span className="bg-gradient-to-r from-purple-400 to-green-400 bg-clip-text text-transparent">Dia 1.</span>
        </h2>
        <p className="text-zinc-400 text-base md:text-lg mb-10" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Não precisa estar no fundo do poço para pedir ajuda. Você só precisa dar o primeiro passo.
        </p>
        <div className="flex justify-center">
          <CTAGroup size="lg" />
        </div>
      </div>
    </Section>
  );
}

/* ══════════════════════════════════════════════
   FOOTER
   ══════════════════════════════════════════════ */
function Footer() {
  return (
    <footer className="border-t border-white/10 bg-[#0F0F1A] py-12" data-testid="landing-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3 text-center md:text-left">
            <img
              src="https://customer-assets.emergentagent.com/job_d4175ee5-624c-4b49-aa70-897a010e6103/artifacts/uccjdqwq_favicon512x512.png"
              alt="U-Clean"
              className="w-10 h-10 object-contain"
              data-testid="footer-logo"
            />
            <div>
              <span className="text-xl font-black tracking-tighter text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>U-Clean</span>
              <p className="text-zinc-500 text-xs italic">Social Network for Addicts</p>
            </div>
          </div>
          <nav className="flex flex-wrap justify-center gap-6 text-sm text-zinc-500" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Link to="/sobre" className="hover:text-white transition-colors">Sobre</Link>
            <Link to="/privacidade" className="hover:text-white transition-colors">Privacidade</Link>
            <Link to="/termos" className="hover:text-white transition-colors">Termos de Uso</Link>
            <a href="mailto:contato@ucleanapp.com.br" className="hover:text-white transition-colors">Contato</a>
          </nav>
        </div>
        <p className="text-center text-zinc-600 text-xs mt-8" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Ambiente 100% moderado e seguro. Seus dados nunca serão compartilhados.
        </p>

        {/* Visual Soul */}
        <div className="flex flex-col items-center gap-3 mt-10 pt-8 border-t border-white/5">
          <img
            src="https://customer-assets.emergentagent.com/job_d4175ee5-624c-4b49-aa70-897a010e6103/artifacts/52xmh6lm_visual%20soul%20logo%20redondo.png"
            alt="Visual Soul"
            className="w-10 h-10 object-contain"
            data-testid="visual-soul-logo"
          />
          <p className="text-zinc-500 text-xs text-center leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
            U-Clean é propriedade da <span className="text-zinc-400 font-medium">Visual Soul Tecnologia e Serviços Digitais Ltda</span>
            <br />
            contato: <a href="mailto:visualsoulcommunity@gmail.com" className="text-zinc-400 hover:text-white transition-colors">visualsoulcommunity@gmail.com</a>
          </p>
        </div>

        {/* AWA Token */}
        <div className="flex flex-col items-center gap-2.5 mt-8 pt-6 border-t border-white/5" data-testid="awa-token-footer">
          <img
            src="/awa-logo.png"
            alt="AWA Token"
            className="w-8 h-8 rounded-full object-contain"
          />
          <p className="text-zinc-600 text-[10px] text-center leading-relaxed max-w-md" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Visual Soul detentora oficial do Token Oficial: <span className="text-zinc-500">AWARE V1 (AWA)</span>
            <br />
            Contrato: <span className="text-zinc-500 font-mono text-[9px]">0x76b7a4dfef135ddb9519c698768704a78b670e07</span>
            <br />
            e-mail: <a href="mailto:contato.celso@visualsoul.com" className="text-zinc-500 hover:text-zinc-400 transition-colors">contato.celso@visualsoul.com</a>
          </p>
        </div>
      </div>
    </footer>
  );
}

/* ══════════════════════════════════════════════
   AWA TOKEN BANNER
   ══════════════════════════════════════════════ */
function AwareBanner() {
  return (
    <section className="py-16 px-4 sm:px-6" data-testid="awa-banner">
      <div className="max-w-4xl mx-auto">
        <Link to="/aware" className="group block relative rounded-2xl overflow-hidden border border-white/5 hover:border-amber-500/30 transition-all duration-500">
          <div className="absolute inset-0 bg-gradient-to-r from-amber-500/5 via-fuchsia-500/5 to-cyan-500/5 group-hover:from-amber-500/10 group-hover:via-fuchsia-500/8 group-hover:to-cyan-500/10 transition-all duration-500" />
          <div className="relative flex flex-col sm:flex-row items-center gap-6 p-8 sm:p-10">
            <div className="relative flex-shrink-0">
              <div className="absolute inset-0 rounded-full bg-amber-500/20 blur-xl scale-150 group-hover:scale-175 transition-transform duration-500" />
              <img src="/aware-color.png" alt="AWARE Token" className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-full" />
            </div>
            <div className="text-center sm:text-left flex-1">
              <p className="text-[10px] font-bold uppercase tracking-widest text-amber-400 mb-1">Conheça o Token Oficial</p>
              <h3 className="text-xl sm:text-2xl font-black text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                AWARE V1 (AWA)
              </h3>
              <p className="text-sm text-zinc-400 leading-relaxed max-w-lg">
                O ativo digital que recompensa sua recuperação. Protocolo Proof of Recovery na Polygon Mainnet.
              </p>
            </div>
            <div className="flex-shrink-0">
              <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold bg-gradient-to-r from-amber-500 to-amber-600 text-black group-hover:from-amber-400 group-hover:to-amber-500 transition-all shadow-lg shadow-amber-500/20">
                Explorar <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </span>
            </div>
          </div>
        </Link>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════
   LANDING PAGE (Main Export)
   ══════════════════════════════════════════════ */
export function Landing() {
  const [loginOpen, setLoginOpen] = useState(false);
  const [landingParams] = useSearchParams();
  const [registerOpen, setRegisterOpen] = useState(() => !!landingParams.get('ref'));

  const handleGoogle = () => {
    const pendingRef = new URLSearchParams(window.location.search).get('ref') || document.getElementById('modal-register-invite')?.value;
    if (pendingRef) localStorage.setItem('uclean_pending_ref', pendingRef.trim());
    const redirectUrl = window.location.origin + '/auth/google/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="bg-[#0F0F1A] min-h-screen" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <Header onLogin={() => setLoginOpen(true)} onRegister={() => setRegisterOpen(true)} onGoogle={handleGoogle} />
      <Hero onLogin={() => setLoginOpen(true)} onRegister={() => setRegisterOpen(true)} onGoogle={handleGoogle} />
      <Manifesto />
      <Pillars />
      <Ecosystem />
      <SocialProof />
      <Pricing />
      <FinalCTA />
      <AwareBanner />
      <Footer />
      <LoginModal open={loginOpen} onClose={() => setLoginOpen(false)} />
      <RegisterModal open={registerOpen} onClose={() => setRegisterOpen(false)} />
    </div>
  );
}
